/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
import { setRedirectSuppressed } from '../utils/redirectCookie';

const REDIRECT_URLS = {
	orell: 'https://bookcircle.orellfuessli.ch',
	thalia: 'https://bookcircle.thalia.de',
};

/**
 * The community suggested to a visitor, plus the URL to send them to.
 */
export interface RedirectTarget {
	suffix: string;
	url: string;
}

function buildRedirectUrl(baseUrl: string): string {
	const utmQuery = app.forum.attribute('redirectUtmQuery');
	return utmQuery ? `${baseUrl}?${utmQuery}` : baseUrl;
}

/**
 * Returns the redirect target if the visitor should be suggested another community,
 * or null if no redirect applies.
 *
 * @param countryCode - ISO country code from GeoIP lookup
 */
export function getRedirectTarget(countryCode: string | null): RedirectTarget | null {
	if (!countryCode) return null;

	const isOrell = app.forum.attribute('isOrell');
	const isThalia = app.forum.attribute('isThalia');

	// On Orell Füssli, suggest Thalia for DE/AT visitors
	if (isOrell && (countryCode === 'DE' || countryCode === 'AT')) {
		return { suffix: 'suggest_thalia', url: buildRedirectUrl(REDIRECT_URLS.thalia) };
	}

	// On Thalia, suggest Orell Füssli for CH visitors
	if (isThalia && countryCode === 'CH') {
		return { suffix: 'suggest_orell', url: buildRedirectUrl(REDIRECT_URLS.orell) };
	}

	return null;
}

export interface CountryRedirectModalAttrs extends IInternalModalAttrs {
	target: RedirectTarget;
}

export default class CountryRedirectModal<CustomAttrs extends CountryRedirectModalAttrs = CountryRedirectModalAttrs> extends Modal<CustomAttrs> {
	className() {
		return 'CountryRedirectModal';
	}

	title(): Mithril.Children {
		const key = `glowingblue-orell-thalia-common.forum.country_redirect.${this.attrs.target.suffix}.title`;
		return m.trust(app.translator.translations[key] || key);
	}

	onready() {
		this.$('.CountryRedirectModal-buttons .Button--primary').trigger('focus');
	}

	content(): Mithril.Children {
		const { suffix, url } = this.attrs.target;

		return (
			<div className="Modal-body">
				<p>{app.translator.trans(`glowingblue-orell-thalia-common.forum.country_redirect.${suffix}.body`)}</p>
				<p className="CountryRedirectModal-question">
					{app.translator.trans(`glowingblue-orell-thalia-common.forum.country_redirect.${suffix}.question`)}
				</p>
				<div className="CountryRedirectModal-buttons">
					<a href={url} className="Button Button--primary" onclick={() => setRedirectSuppressed()}>
						{app.translator.trans('glowingblue-orell-thalia-common.forum.country_redirect.button_switch')}
					</a>
					<Button
						className="Button Button--secondary"
						onclick={() => {
							setRedirectSuppressed();
							this.hide();
						}}
					>
						{app.translator.trans('glowingblue-orell-thalia-common.forum.country_redirect.button_stay')}
					</Button>
				</div>
			</div>
		);
	}
}
